import { useState, useEffect, useCallback, useRef } from "react"
import SelectField from '../common/SelectField.js';
import { useParams, useNavigate } from "react-router-dom"
import { shipmentService } from "../../services/shipmentService"
import { paymentService } from '../../services/ShipmentPaymentService.js';
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical, Trash2, Eye, MapPin, Printer } from "lucide-react";
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { printPdfBlob, parseBlobErrorMessage } from '../../utils/printPdfBlob.js'
import { openHtmlPrintWindow } from '../../utils/openHtmlPrintWindow.js'
import { useTranslation } from 'react-i18next';
import {
  PackageDimensionSummary,
  ViewPackageDimensionModal,
} from './package-dimension/index.js';
import { parcelsFromApi, busArticlesFromApi } from '../../utils/cargoParcelUtils.js';
import BusArticlePackagesForm from './BusArticlePackagesForm.js';
import ShipmentMediaGallery from './ShipmentMediaGallery.js';
import { formatAuditUser } from '../../utils/userDisplay.js';
import { formatIstDateTime } from '../../utils/localDate.js';
import ShipmentTrackingPanel from '../tracking/ShipmentTrackingPanel.js';
import CancelShipmentModal from './CancelShipmentModal.js';
import {
  cancelledByLabel,
  cancelRequestedByLabel,
  displayShipmentStatus,
  isCancelPending,
  isCancelledShipment,
} from '../../utils/shipmentCancel.js';

/** Hide null / empty / N/A text fields */
const hasFilledValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'number') return !Number.isNaN(value);
  const text = String(value).trim();
  if (!text) return false;
  if (text.toUpperCase() === 'N/A') return false;
  return true;
};

/** Hide null / empty / zero amounts for optional charge fields */
const hasFilledAmount = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const num = Number(value);
  return !Number.isNaN(num) && num !== 0;
};

const CHARGE_PAYMENT_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
];

function PaymentValueBox({ label, value, boxClassName = '', valueClassName = '' }) {
  return (
    <div
      className={`bg-gray-50 border border-gray-200 rounded-xl p-3 min-w-0 ${boxClassName}`}
    >
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      <p className={`font-medium text-gray-700 text-sm sm:text-base mt-1 break-words ${valueClassName}`}>
        {value}
      </p>
    </div>
  );
}

function InfoField({ label, value, className = '' }) {
  if (!hasFilledValue(value)) return null;
  return (
    <div className={className}>
      <p className="text-xs sm:text-sm text-gray-600 font-semibold">{label}</p>
      <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
        {value}
      </p>
    </div>
  );
}

function SectionCard({ title, badge, children, className = 'mb-4 sm:mb-6' }) {
  return (
    <div className={`bg-white rounded-xl shadow-md p-4 sm:p-5 ${className}`}>
      <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        {title}
        {badge}
      </h2>
      {children}
    </div>
  );
}

const ViewShipment = ({ shipmentId: shipmentIdProp, embedded = false, onClose } = {}) => {
  const { t } = useTranslation();
  const { shipmentId: shipmentIdParam } = useParams()
  const shipmentId = shipmentIdProp || shipmentIdParam
  const navigate = useNavigate()
  const { user } = useAuth()
  const [shipment, setShipment] = useState(null)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false)

  const [showDeliveryChargeModal, setShowDeliveryChargeModal] = useState(false)
  const [showLabourChargeModal, setShowLabourChargeModal] = useState(false)
  const [showLateChargeModal, setShowLateChargeModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showViewPackageModal, setShowViewPackageModal] = useState(false)
  const [showTrackingModal, setShowTrackingModal] = useState(false)
  const [trackingData, setTrackingData] = useState(null)
  const [loadingTracking, setLoadingTracking] = useState(false)
  const [trackingError, setTrackingError] = useState('')
  const [openPaymentActionId, setOpenPaymentActionId] = useState(null)
  const [showMoreActions, setShowMoreActions] = useState(false)
  const moreActionsRef = useRef(null)
  const [cancelModal, setCancelModal] = useState({ open: false, mode: 'request' })
  const [cancelSubmitting, setCancelSubmitting] = useState(false)

  const [deliveryChargeAmount, setDeliveryChargeAmount] = useState("")
  const [labourChargeAmount, setLabourChargeAmount] = useState("")
  const [lateChargeAmount, setLateChargeAmount] = useState("")
  const [addingCharge, setAddingCharge] = useState(false)
  const [addingLateCharge, setAddingLateCharge] = useState(false)

  const [deliveryChargePaymentMode, setDeliveryChargePaymentMode] = useState("cash")
  const [labourChargePaymentMode, setLabourChargePaymentMode] = useState("cash")
  const [lateChargePaymentMode, setLateChargePaymentMode] = useState("cash")

  const [ewayBills, setEwayBills] = useState([])
  const [loadingEwayBills, setLoadingEwayBills] = useState(false)

  const isAdminRole = user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"
  const canEditShipment =
    isCancelledShipment(shipment) || isCancelPending(shipment)
      ? false
      : user?.role === "System Admin" || user?.role === "Admin"
        ? true
        : typeof shipment?.can_edit === "boolean"
          ? shipment.can_edit
          : false
  const canAddPayment =
    Number(shipment?.balance_amount || 0) > 0 &&
    (isAdminRole ||
      (user?.role === "Agent" &&
        String(shipment?.user_id || "") === String(user?.user_id || "")))
  // Delivery / labour / late charges: booking or receiver agent (or admin) only —
  // not other agents who opened the shipment via Track.
  const canAddCharges =
    typeof shipment?.can_add_charges === 'boolean'
      ? shipment.can_add_charges
      : isAdminRole ||
        (user?.role === 'Agent' &&
          (String(shipment?.user_id || '') === String(user?.user_id || '') ||
            String(shipment?.receiver_agent_user_id || '') === String(user?.user_id || '')))

  const fetchShipmentDetails = useCallback(async () => {
    try {
      setLoading(true)
      const shipmentRes = await shipmentService.getShipment(shipmentId)
      setShipment(shipmentRes.data.shipment || shipmentRes.data.data)
    } catch (error) {
      console.error("Error fetching shipment:", error)
      Alert.error("Failed to load shipment details")
      if (embedded && onClose) onClose()
      else navigate("/shipments")
    } finally {
      setLoading(false)
    }
  }, [shipmentId, navigate, embedded, onClose])

  const handleCancelSubmit = async (reason) => {
    try {
      setCancelSubmitting(true)
      if (cancelModal.mode === 'approve') {
        await shipmentService.approveCancel(shipmentId, reason)
        Alert.success('Shipment cancelled')
      } else if (cancelModal.mode === 'reject') {
        await shipmentService.rejectCancel(shipmentId, reason)
        Alert.success('Cancel request rejected')
      } else {
        const res = await shipmentService.requestCancel(shipmentId, reason)
        Alert.success(res.data?.message || 'Cancel submitted')
      }
      setCancelModal({ open: false, mode: 'request' })
      await fetchShipmentDetails()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to update cancellation')
    } finally {
      setCancelSubmitting(false)
    }
  }

  const fetchPaymentHistory = useCallback(async () => {
    try {
      setLoadingPaymentHistory(true)
      const response = await paymentService.getPaymentHistory(shipmentId)
      // Fix: Access payment_history from nested data object
      const historyData = response.data?.data?.payment_history || response.data?.payment_history || []
      setPaymentHistory(historyData)
    } catch (error) {
      console.error("Error fetching payment history:", error)
      setPaymentHistory([])
    } finally {
      setLoadingPaymentHistory(false)
    }
  }, [shipmentId])

  const fetchEwayBills = useCallback(async () => {
    try {
      setLoadingEwayBills(true)
      const response = await shipmentService.getEwayBills(shipmentId)
      setEwayBills(response.data.eway_bills || [])
    } catch (error) {
      console.error("Error fetching e-way bills:", error)
      setEwayBills([])
    } finally {
      setLoadingEwayBills(false)
    }
  }, [shipmentId])

  useEffect(() => {
    fetchShipmentDetails()
    fetchPaymentHistory()
    fetchEwayBills()
  }, [fetchShipmentDetails, fetchPaymentHistory])

  useEffect(() => {
    if (!showMoreActions) return undefined

    const handleClickOutside = (event) => {
      if (moreActionsRef.current && !moreActionsRef.current.contains(event.target)) {
        setShowMoreActions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [showMoreActions])

  const closeMoreActions = () => setShowMoreActions(false)

  const openTrackingModal = async () => {
    setShowTrackingModal(true)
    setLoadingTracking(true)
    setTrackingError('')
    try {
      const response = await shipmentService.getTrackingInfo(shipmentId)
      const payload = response?.data || {}
      if (payload.success === false) {
        setTrackingData(null)
        setTrackingError(payload.error || 'Tracking not available')
        return
      }
      setTrackingData(payload)
    } catch (error) {
      setTrackingData(null)
      setTrackingError(error.response?.data?.error || 'Failed to load tracking')
    } finally {
      setLoadingTracking(false)
    }
  }

  const downloadInvoice = async () => {
    try {
      const response = await shipmentService.downloadInvoice(shipmentId)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `invoice_${shipmentId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      Alert.success("Invoice downloaded successfully")
    } catch (error) {
      console.error("Error downloading invoice:", error)
      Alert.error("Failed to download invoice")
    }
  }

  const handlePrintInvoice = async () => {
    try {
      const response = await shipmentService.downloadInvoice(shipmentId)
      const result = await printPdfBlob(response.data, `invoice_${shipmentId}.pdf`)
      if (result.method === 'download') {
        Alert.success('Popup blocked — invoice PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      console.error("Print invoice error:", error)
      Alert.error(await parseBlobErrorMessage(error, "Failed to print invoice"))
    }
  }

  const downloadReceipt = async () => {
    try {
      const response = await shipmentService.downloadReceipt(shipmentId)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `receipt_${shipmentId}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      Alert.success("Receipt downloaded successfully")
    } catch (error) {
      console.error("Error downloading receipt:", error)
      Alert.error(await parseBlobErrorMessage(error, "Failed to download receipt"))
    }
  }

  const handlePrintReceipt = async () => {
    try {
      const response = await shipmentService.printReceipt(shipmentId)
      const html = typeof response.data === "string" ? response.data : ""
      if (!openHtmlPrintWindow(html)) {
        Alert.error("Popup blocked — allow popups to print the receipt")
      }
    } catch (error) {
      console.error("Print receipt error:", error)
      Alert.error(error.response?.data?.error || "Failed to print receipt")
    }
  }

  const handlePrintEwayBill = async (billId = null) => {
    try {
      const response = await shipmentService.printEwayBill(shipmentId, billId)
      const result = await printPdfBlob(response.data, `eway_bill_${shipmentId}.pdf`)
      if (result.method === 'download') {
        Alert.success('Popup blocked — e-Way Bill PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      console.error("Print e-way bill error:", error)
      Alert.error(await parseBlobErrorMessage(error, "Failed to print e-Way Bill"))
    }
  }

  const handlePrintQR = async () => {
    try {
      const response = await shipmentService.getQRCode(shipmentId)
      const result = await printPdfBlob(response.data, `QR_Code_${shipmentId}.pdf`)
      if (result.method === 'download') {
        Alert.success('Popup blocked — QR PDF downloaded. Open the file to print.')
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, "Failed to print QR code"))
    }
  }

  const handleAddDeliveryCharge = async () => {
    const amount = Number.parseFloat(deliveryChargeAmount)

    if (isNaN(amount) || amount <= 0) {
      Alert.error("Please enter a valid delivery charge amount")
      return
    }

    if (!deliveryChargePaymentMode) {
      Alert.error("Please select a payment mode")
      return
    }

    const confirmed = await Alert.confirm(
      `Add delivery charge of ₹${amount.toFixed(2)} via ${deliveryChargePaymentMode.toUpperCase()} to shipment ${shipmentId}?`,
      "Confirm Delivery Charge",
    )

    if (!confirmed) return

    try {
      setAddingCharge(true)
      await shipmentService.addDeliveryCharge(shipmentId, amount, deliveryChargePaymentMode)

      Alert.success("Delivery charge added successfully")
      setShowDeliveryChargeModal(false)
      setDeliveryChargeAmount("")
      setDeliveryChargePaymentMode("cash")

      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to add delivery charge")
    } finally {
      setAddingCharge(false)
    }
  }

  const handleAddLabourCharge = async () => {
    const amount = Number.parseFloat(labourChargeAmount)

    if (isNaN(amount) || amount <= 0) {
      Alert.error("Please enter a valid labour charge amount")
      return
    }

    if (!labourChargePaymentMode) {
      Alert.error("Please select a payment mode")
      return
    }

    const confirmed = await Alert.confirm(
      `Add labour charge of ₹${amount.toFixed(2)} via ${labourChargePaymentMode.toUpperCase()} to shipment ${shipmentId}?`,
      "Confirm Labour Charge",
    )

    if (!confirmed) return

    try {
      setAddingCharge(true)
      await shipmentService.addLabourCharge(shipmentId, amount, labourChargePaymentMode)

      Alert.success("Labour charge added successfully")
      setShowLabourChargeModal(false)
      setLabourChargeAmount("")
      setLabourChargePaymentMode("cash")

      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to add labour charge")
    } finally {
      setAddingCharge(false)
    }
  }

  const handleAddLateCharge = async () => {
    const amount = Number.parseFloat(lateChargeAmount)

    if (isNaN(amount) || amount <= 0) {
      Alert.error("Please enter a valid late charge amount")
      return
    }

    if (!lateChargePaymentMode) {
      Alert.error("Please select a payment mode")
      return
    }

    const confirmed = await Alert.confirm(
      `Add late charge of ₹${amount.toFixed(2)} via ${lateChargePaymentMode.toUpperCase()} to shipment ${shipmentId}?`,
      "Confirm Late Charge",
    )

    if (!confirmed) return

    try {
      setAddingLateCharge(true)
      await paymentService.addLateCharge(shipmentId, amount, lateChargePaymentMode)

      Alert.success("Late charge added successfully")
      setShowLateChargeModal(false)
      setLateChargeAmount("")
      setLateChargePaymentMode("cash")

      await fetchShipmentDetails()
      await fetchPaymentHistory()
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to add late charge")
    } finally {
      setAddingLateCharge(false)
    }
  }

  const handleDeletePayment = async (payment) => {
    const confirmed = await Alert.confirm(
      `Delete payment record of ₹${payment.payment_amount?.toFixed(2)}?`,
      'Delete Payment Record'
    )
    if (!confirmed) return
    try {
      await paymentService.deletePayment(payment.id)
      Alert.success('Payment record deleted successfully')
      await fetchPaymentHistory()
      await fetchShipmentDetails()
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to delete payment')
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      Booked: "bg-blue-100 text-blue-800 border-blue-300",
      "In Transit": "bg-indigo-100 text-indigo-800 border-indigo-300",
      Dispatched: "bg-purple-100 text-purple-800 border-purple-300",
      Received: "bg-yellow-100 text-yellow-800 border-yellow-300",
      Delivered: "bg-green-100 text-green-800 border-green-300",
      Returned: "bg-red-100 text-red-800 border-red-300",
      Rejected: "bg-gray-100 text-gray-500 border-gray-300",
      Cancelled: "bg-gray-100 text-gray-500 border-gray-300",
      "Cancel pending": "bg-amber-100 text-amber-800 border-amber-300",
    }
    return colors[status] || "bg-gray-100 text-gray-800 border-gray-300"
  }

  const isLabourChargeDisabled = false
  const isDeliveryChargeDisabled = false

  const wrapPage = (node) => (
    embedded ? <div className="bg-gray-50">{node}</div> : <DashboardLayout>{node}</DashboardLayout>
  )

  if (loading) {
    return wrapPage(
      <div className="flex items-center justify-center py-20">
        <Loader fullScreen={false} size="default" message="Loading shipment details..." />
      </div>
    )
  }

  if (!shipment) {
    return wrapPage(
      <div className="flex items-center justify-center min-h-[40vh] sm:min-h-[60vh]">
        <div className="text-center px-4">
          <p className="text-gray-600 font-semibold text-base sm:text-lg">Shipment not found</p>
          {embedded && onClose && (
            <Button variant="secondary" className="mt-4" onClick={onClose}>Close</Button>
          )}
        </div>
      </div>
    )
  }

  return wrapPage(
    <div className={embedded ? 'p-3 sm:p-4' : ''}>
                {/* Header */}
                <div className="flex flex-col gap-3 lg:flex-row lg:justify-between lg:items-center mb-4 sm:mb-6">
                  <div className={embedded ? 'hidden sm:block min-w-0' : 'min-w-0'}>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-700 flex flex-wrap items-center gap-2 sm:gap-3">
                      <Package className="w-6 h-6 text-buttonhoverTxt shrink-0" />Shipment Details
                    </h1>
                  </div>
                  <div className="flex flex-col gap-2 w-full lg:w-auto md:flex-row md:flex-wrap md:gap-3">
                    {canEditShipment && (
                      <Button
                        variant="secondary"
                        icon={Pencil}
                        onClick={() => navigate(`/shipments/edit/${shipmentId}`)}
                        className="hidden md:inline-flex"
                      >
                        Edit
                      </Button>
                    )}
                    {(shipment?.can_request_cancel || shipment?.can_cancel_now) && !shipment?.can_approve_cancel && (
                      <Button
                        variant="danger"
                        onClick={() => setCancelModal({ open: true, mode: 'request' })}
                        className="hidden md:inline-flex"
                      >
                        Cancel
                      </Button>
                    )}
                    {shipment?.can_approve_cancel && (
                      <Button
                        variant="success"
                        onClick={() => setCancelModal({ open: true, mode: 'approve' })}
                        className="hidden md:inline-flex"
                      >
                        Approve cancel
                      </Button>
                    )}
                    {shipment?.can_reject_cancel_request && (
                      <Button
                        variant="secondary"
                        onClick={() => setCancelModal({ open: true, mode: 'reject' })}
                        className="hidden md:inline-flex"
                      >
                        Reject request
                      </Button>
                    )}
                    {!loadingEwayBills && ewayBills.length > 0 && (
                      <Button
                        variant="secondary"
                        icon={Printer}
                        onClick={() => handlePrintEwayBill()}
                        className="hidden md:inline-flex"
                      >
                        Print e-Way Bill
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      icon={MapPin}
                      onClick={openTrackingModal}
                      className="hidden md:inline-flex"
                    >
                      Tracking
                    </Button>

                    <div className="flex items-center justify-end gap-2 w-full md:w-auto md:contents">
                      <Button
                        variant="secondary"
                        icon={embedded ? undefined : ArrowLeft}
                        mobileIconOnly={!embedded}
                        mobileLabel={embedded ? "Close" : "Back to Shipments"}
                        onClick={() => {
                          if (embedded && onClose) onClose()
                          else navigate("/shipments")
                        }}
                        className={`md:flex-none ${embedded ? 'hidden sm:inline-flex' : ''}`}
                      >
                        {embedded ? "Close" : "Back to Shipments"}
                      </Button>
                      <div ref={moreActionsRef} className="relative md:flex-none">
                        <Button
                          variant="primary"
                          icon={MoreVertical}
                          mobileIconOnly
                          mobileLabel="More Actions"
                          onClick={() => setShowMoreActions((open) => !open)}
                          aria-expanded={showMoreActions}
                          className="md:w-auto"
                        >
                          More Actions
                        </Button>

                        {showMoreActions && (
                        <div className="absolute right-0 md:left-0 mt-1 w-48 bg-white shadow-lg rounded-xl border border-gray-200 z-[120] overflow-hidden">

                        {canEditShipment && (
                          <button
                            onClick={() => {
                              closeMoreActions()
                              navigate(`/shipments/edit/${shipmentId}`)
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition flex items-center gap-2"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                        {(shipment?.can_request_cancel || shipment?.can_cancel_now) && !shipment?.can_approve_cancel && (
                          <button
                            onClick={() => {
                              closeMoreActions()
                              setCancelModal({ open: true, mode: 'request' })
                            }}
                            className="md:hidden w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition"
                          >
                            Cancel shipment
                          </button>
                        )}
                        {shipment?.can_approve_cancel && (
                          <button
                            onClick={() => {
                              closeMoreActions()
                              setCancelModal({ open: true, mode: 'approve' })
                            }}
                            className="md:hidden w-full text-left px-4 py-2 text-sm text-emerald-700 hover:bg-emerald-50 transition"
                          >
                            Approve cancel
                          </button>
                        )}
                        {shipment?.can_reject_cancel_request && (
                          <button
                            onClick={() => {
                              closeMoreActions()
                              setCancelModal({ open: true, mode: 'reject' })
                            }}
                            className="md:hidden w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                          >
                            Reject request
                          </button>
                        )}

                        <button
                          onClick={() => {
                            closeMoreActions()
                            openTrackingModal()
                          }}
                          className="md:hidden w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition flex items-center gap-2"
                        >
                          <MapPin className="w-4 h-4" />
                          Tracking
                        </button>

                        <button
                          onClick={() => {
                            closeMoreActions()
                            downloadInvoice()
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                        >
                          Download Invoice
                        </button>

                        <button
                          onClick={() => {
                            closeMoreActions()
                            handlePrintInvoice()
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                        >
                          🖨️ Print Invoice
                        </button>

                        <button
                          onClick={() => {
                            closeMoreActions()
                            downloadReceipt()
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                        >
                          🧾 Download Receipt
                        </button>

                        <button
                          onClick={() => {
                            closeMoreActions()
                            handlePrintReceipt()
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                        >
                          🖨️ Print Receipt
                        </button>

                        <button
                          onClick={() => {
                            closeMoreActions()
                            handlePrintQR()
                          }}
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                        >
                          🔳 Print QR Code
                        </button>

                        {ewayBills.length > 0 && (
                          <button
                            onClick={() => {
                              closeMoreActions()
                              handlePrintEwayBill()
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                          >
                            🖨️ Print e-Way Bill
                          </button>
                        )}

                        {canAddCharges && (
                          <>
                            <button
                              onClick={() => {
                                closeMoreActions()
                                setShowDeliveryChargeModal(true)
                              }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                            >
                              Add Delivery Charge
                            </button>

                            <button
                              onClick={() => {
                                closeMoreActions()
                                setShowLabourChargeModal(true)
                              }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                            >
                              Add Labour Charge
                            </button>

                            <button
                              onClick={() => {
                                closeMoreActions()
                                setShowLateChargeModal(true)
                              }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition"
                            >
                              Add Late Charge
                            </button>
                          </>
                        )}
                      </div>
                        )}
                    </div>
                    </div>
                  </div>
                </div>

                {/* Status Cards */}
                <div className="mb-4 sm:mb-6">

                  <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4">

                    {/* GC Number */}
                    <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-orange-500">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        GC Number
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-orange-600 font-mono">
                        {shipment.shipment_id}
                      </p>
                    </div>

                    {/* Consignment No */}
                    <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-amber-500">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Consignment No
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-amber-700 font-mono">
                        {shipment.consignment_no || "—"}
                      </p>
                    </div>

                    {(user?.role === "Admin" || user?.role === "Accountant") && (
                      <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-rose-500">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                          Receipt Prints
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-rose-600 font-mono">
                          {Number(shipment.receipt_print_count || 0)}
                        </p>
                      </div>
                    )}

                    {/* Status */}
                    <div className={`bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 ${isCancelledShipment(shipment) ? 'border-gray-400' : isCancelPending(shipment) ? 'border-amber-400' : 'border-blue-500'}`}>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Status
                      </p>
                      <span
                        className={`inline-block px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase border ${getStatusColor(
                          displayShipmentStatus(shipment)
                        )}`}
                      >
                        {displayShipmentStatus(shipment)}
                      </span>
                      {isCancelledShipment(shipment) && (
                        <div className="mt-3 text-sm text-gray-600 space-y-1">
                          <p><span className="font-semibold">Reason:</span> {shipment.cancel_reason || '—'}</p>
                          <p><span className="font-semibold">Cancelled by:</span> {cancelledByLabel(shipment)}</p>
                          {shipment.cancel_requested_by && (
                            <p><span className="font-semibold">Requested by:</span> {cancelRequestedByLabel(shipment)}</p>
                          )}
                        </div>
                      )}
                      {isCancelPending(shipment) && shipment.cancel_reason && (
                        <p className="mt-3 text-sm text-amber-800">
                          Cancel requested: {shipment.cancel_reason}
                        </p>
                      )}
                    </div>

                    {/* Total Amount */}
                    <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-green-500">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Total Amount
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-green-600">
                        ₹{shipment.total_amount?.toFixed(2)}
                      </p>
                    </div>

                    {/* Balance Due */}
                    <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border-l-4 border-red-500">
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">
                        Balance Due
                      </p>
                      <p className="text-lg sm:text-2xl font-bold text-red-600">
                        ₹{shipment.balance_amount?.toFixed(2)}
                      </p>
                    </div>

                  </div>
                </div>

                <SectionCard title="Booking & audit">
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Booked by</p>
                      <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-words">
                        {formatAuditUser(shipment, 'created_by')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Booked on</p>
                      <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                        {formatIstDateTime(shipment.created_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Last updated by</p>
                      <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1 break-words">
                        {shipment.updated_by || shipment.updated_by_display || shipment.updated_by_name
                          ? formatAuditUser(shipment, 'updated_by')
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600 font-semibold">Last updated on</p>
                      <p className="font-medium text-gray-700 text-sm sm:text-base bg-gray-100 p-2 py-1 mt-1">
                        {formatIstDateTime(shipment.updated_at)}
                      </p>
                    </div>
                  </div>
                </SectionCard>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-6 min-w-0">
                  {/* Consigner Information */}
                  <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 min-w-0">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Consigner Information
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-5">
                      <InfoField
                        label="Name"
                        value={shipment.consigner_name || shipment.sender_name}
                        className="mt-2"
                      />
                      <InfoField
                        label="Contact"
                        value={shipment.consigner_contact || shipment.sender_contact}
                      />
                      <InfoField
                        label="Email"
                        value={shipment.consigner_email || shipment.sender_email}
                      />
                      <InfoField
                        label={t('pages.shipment.destination')}
                        value={shipment.consigner_location || shipment.sender_location}
                      />
                      <InfoField
                        label="Consigner GST"
                        value={shipment.consigner_gst || shipment.sender_gst}
                      />
                    </div>
                    <InfoField
                      label={t('common.address')}
                      value={shipment.consigner_address || shipment.sender_address}
                      className="mt-4 sm:mt-6"
                    />
                  </div>

                  {/* Consignee Information */}
                  <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 min-w-0">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Consignee Information
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 sm:gap-5">
                      <InfoField
                        label="Name"
                        value={shipment.consignee_name || shipment.receiver_name}
                      />
                      <InfoField
                        label="Contact"
                        value={shipment.consignee_contact || shipment.receiver_contact}
                      />
                      <InfoField
                        label="Email"
                        value={shipment.consignee_email || shipment.receiver_email}
                      />
                      <InfoField
                        label={t('pages.shipment.destination')}
                        value={shipment.consignee_location || shipment.receiver_location}
                      />
                      <InfoField
                        label="Consignee GST"
                        value={shipment.consignee_gst || shipment.receiver_gst}
                      />
                    </div>
                    <InfoField
                      label={t('common.address')}
                      value={shipment.consignee_address || shipment.receiver_address}
                      className="mt-4 sm:mt-6"
                    />
                  </div>
                </div>

                {/* E-way Bills — only when bills exist */}
                {!loadingEwayBills && ewayBills.length > 0 && (
                  <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      E-way Bills
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {ewayBills.map((bill, index) => (
                        <div key={bill.id} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3">
                          <p className="text-xs text-gray-600 font-semibold mb-1">E-way Bill #{index + 1}</p>
                          <p className="font-mono font-bold text-blue-800 text-sm">{bill.eway_bill_number}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {bill.source === 'api' ? 'API' : 'Manual'}
                            {bill.status === 'cancelled' ? ' · Cancelled' : ''}
                            {bill.vehicle_no ? ` · ${bill.vehicle_no}` : ''}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Added: {formatIstDateTime(bill.created_at)}
                          </p>
                          <button
                            type="button"
                            onClick={() => handlePrintEwayBill(bill.id)}
                            className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-900"
                          >
                            Print
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Package dimensions (cargo) — compact summary; details in modal */}
                {shipment.shipment_type === 'cargo' && Array.isArray(shipment.parcels) && shipment.parcels.length > 0 && (
                  <div className="mb-4 sm:mb-6">
                    <PackageDimensionSummary
                      parcels={parcelsFromApi(shipment.parcels)}
                      mode="view"
                      onView={() => setShowViewPackageModal(true)}
                    />
                  </div>
                )}

                {/* Bus multi-article packages */}
                {shipment.shipment_type !== 'cargo' && (
                  <div className="mb-4 sm:mb-6">
                    <BusArticlePackagesForm
                      articles={busArticlesFromApi(shipment.parcels, shipment)}
                      readOnly
                      freightByWeight={
                        Number(shipment.rate_per_kg) > 0 &&
                        !(Array.isArray(shipment.parcels) &&
                          shipment.parcels.some((p) => Number(p.article_amount) > 0))
                      }
                    />
                  </div>
                )}

                {/* Shipment Weight */}
                {(() => {
                  const weightItems = [];
                  if (hasFilledValue(shipment.weight)) {
                    weightItems.push(
                      <PaymentValueBox
                        key="weight"
                        label="Weight"
                        value={`${shipment.weight} kg`}
                      />,
                    );
                  }
                  if (hasFilledAmount(shipment.rate_per_kg)) {
                    weightItems.push(
                      <PaymentValueBox
                        key="rate"
                        label="Rate / KG"
                        value={`₹${Number(shipment.rate_per_kg).toFixed(2)}`}
                      />,
                    );
                  }
                  if (weightItems.length === 0) return null;
                  return (
                    <SectionCard title="Shipment Weight">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                        {weightItems}
                      </div>
                    </SectionCard>
                  );
                })()}

                {/* Other Information */}
                {(() => {
                  const otherItems = [];
                  // Bus article type/rate shown in BusArticlePackagesForm
                  if (hasFilledAmount(shipment.shipment_value)) {
                    otherItems.push(
                      <PaymentValueBox
                        key="shipment_value"
                        label="Customer Value of the Goods"
                        value={`₹${Number(shipment.shipment_value).toFixed(2)}`}
                      />,
                    );
                  }
                  if (hasFilledValue(shipment.customer_invoice_number)) {
                    otherItems.push(
                      <PaymentValueBox
                        key="customer_invoice_number"
                        label="Customer Invoice Number"
                        value={shipment.customer_invoice_number}
                      />,
                    );
                  }
                  if (hasFilledValue(shipment.invoice_number)) {
                    otherItems.push(
                      <PaymentValueBox
                        key="invoice"
                        label="Invoice No."
                        value={shipment.invoice_number}
                      />,
                    );
                  }
                  if (otherItems.length === 0) return null;
                  return (
                    <SectionCard title="Other Information">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                        {otherItems}
                      </div>
                    </SectionCard>
                  );
                })()}

                {/* Payment Charges */}
                {(() => {
                  const chargeItems = [];
                  const pushCharge = (key, label, amount, prefix = '₹') => {
                    if (!hasFilledAmount(amount)) return;
                    chargeItems.push(
                      <PaymentValueBox
                        key={key}
                        label={label}
                        value={`${prefix}${Number(amount).toFixed(2)}`}
                      />,
                    );
                  };

                  pushCharge('freight', 'Freight Charges', shipment.freight_charges);
                  if (shipment.shipment_type === 'cargo') {
                    pushCharge('loading', 'Loading', shipment.loading_charges);
                    pushCharge('unloading', 'Unloading', shipment.unloading_charges);
                    pushCharge('toll', 'Toll', shipment.toll_charges);
                    pushCharge('fuel', 'Fuel Surcharge', shipment.fuel_surcharge);
                    pushCharge('packing', 'Packing', shipment.packing_charges);
                  } else {
                    pushCharge('handling', 'Handling Charges', shipment.handling_charges);
                  }
                  pushCharge('delivery', 'Delivery Charges', shipment.delivery_charges);
                  pushCharge('door_pickup', 'Door Pickup Charges', shipment.door_pickup_charges);
                  pushCharge('docket', 'Docket Fee', shipment.docket_fee);
                  pushCharge('late', 'Late Charges', shipment.late_charges);
                  pushCharge('other', 'Other Charges', shipment.other_charges);
                  if (hasFilledAmount(shipment.discount)) {
                    chargeItems.push(
                      <PaymentValueBox
                        key="discount"
                        label="Discount"
                        value={`-₹${Number(shipment.discount).toFixed(2)}`}
                      />,
                    );
                  }
                  if (hasFilledAmount(shipment.sgst_amount)) {
                    chargeItems.push(
                      <PaymentValueBox
                        key="sgst"
                        label={`SGST (${shipment.sgst_percentage || 0}%)`}
                        value={`₹${Number(shipment.sgst_amount).toFixed(2)}`}
                      />,
                    );
                  }
                  if (hasFilledAmount(shipment.cgst_amount)) {
                    chargeItems.push(
                      <PaymentValueBox
                        key="cgst"
                        label={`CGST (${shipment.cgst_percentage || 0}%)`}
                        value={`₹${Number(shipment.cgst_amount).toFixed(2)}`}
                      />,
                    );
                  }
                  if (hasFilledAmount(shipment.igst_amount)) {
                    chargeItems.push(
                      <PaymentValueBox
                        key="igst"
                        label={`IGST (${shipment.igst_percentage || 0}%)`}
                        value={`₹${Number(shipment.igst_amount).toFixed(2)}`}
                      />,
                    );
                  }

                  if (chargeItems.length === 0) return null;
                  return (
                    <SectionCard title="Payment Charges">
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                        {chargeItems}
                      </div>
                    </SectionCard>
                  );
                })()}

                {/* Totals Summary */}
                <SectionCard title="Total Weight & Amount">
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                    {hasFilledValue(shipment.weight) && (
                      <PaymentValueBox
                        label="Total Weight"
                        value={`${shipment.weight} kg`}
                        boxClassName="bg-slate-50 border-slate-200"
                        valueClassName="font-bold text-secondary"
                      />
                    )}
                    <PaymentValueBox
                      label="Total Amount"
                      value={`₹${Number(shipment.total_amount || 0).toFixed(2)}`}
                      boxClassName="bg-blue-50 border-blue-200"
                      valueClassName="font-bold text-blue-600"
                    />
                    <PaymentValueBox
                      label="Paid Amount"
                      value={`₹${Number(shipment.paid_amount || 0).toFixed(2)}`}
                      boxClassName="bg-green-50 border-green-200"
                      valueClassName="font-bold text-green-600"
                    />
                    <PaymentValueBox
                      label="Balance Due"
                      value={`₹${Number(shipment.balance_amount || 0).toFixed(2)}`}
                      boxClassName="bg-red-50 border-red-200"
                      valueClassName="font-bold text-red-600"
                    />
                    {hasFilledValue(shipment.mode_of_payment) && (
                      <PaymentValueBox
                        label="Payment Status"
                        value={shipment.mode_of_payment.replace(/_/g, ' ')}
                        valueClassName="uppercase font-semibold"
                      />
                    )}
                  </div>
                </SectionCard>

                {(shipment.images?.length > 0 ||
                  shipment.media?.length > 0 ||
                  shipment.has_parcel_image_1 ||
                  shipment.has_parcel_image_2) && (
                  <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                    <ShipmentMediaGallery
                      media={
                        shipment.images?.length
                          ? shipment.images
                          : shipment.media?.length
                            ? shipment.media
                            : [
                                shipment.parcel_image_1_url && {
                                  media_type: 'photo',
                                  cdn_url: shipment.parcel_image_1_url,
                                },
                                shipment.parcel_image_2_url && {
                                  media_type: 'photo',
                                  cdn_url: shipment.parcel_image_2_url,
                                },
                              ].filter(Boolean)
                      }
                      title="Photos & Videos"
                      emptyMessage=""
                    />
                  </div>
                )}

                <div className="bg-white rounded-xl shadow-md p-4 sm:p-5 mb-4 sm:mb-6">
                  <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center">
                    <h2 className="text-base sm:text-md font-semibold text-secondary pb-0 sm:pb-2 border-b-0 sm:border-b-2 border-grey-400">
                      Payment History
                    </h2>
                    {canAddPayment && (
                      <Button variant="primary" className="w-full sm:w-auto shrink-0" onClick={() => navigate(`/payments/add/${shipmentId}`)}>
                        Add Payment
                      </Button>
                    )}
                  </div>

                  {loadingPaymentHistory ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500"></div>
                      <p className="text-gray-600 font-semibold mt-2">Loading payment history...</p>
                    </div>
                  ) : paymentHistory && paymentHistory.length > 0 ? (
                    <>
                      {/* Desktop / tablet table */}
                      <div className="hidden lg:block overflow-x-auto -mx-1 px-1">
                        <table className="w-full min-w-[720px] border-collapse">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700">Recorded By</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700">{t('common.type')}</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700">Date</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-700">Amount</th>
                              <th className="px-3 py-2 text-right font-semibold text-gray-700">Balance</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700">Remark</th>
                              {isAdminRole && (
                                <th className="px-3 py-2 text-left font-semibold text-gray-700">{t('common.actions')}</th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paymentHistory.map((payment, index) => (
                              <tr key={payment.id || index} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-900 font-semibold">
                                  {payment.recorded_by_name || "N/A"}
                                </td>
                                <td className="px-3 py-2">
                                  <span className="px-2 py-1 rounded-full text-xs font-semibold text-secondary">
                                    {payment.payment_mode?.replace(/_/g, ' ') || payment.type || "Payment"}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-900">
                                  {formatIstDateTime(payment.payment_date || payment.created_at)}
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-green-600">
                                  ₹{payment.payment_amount?.toFixed(2) || payment.amount?.toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-right font-bold text-orange-600">
                                  ₹{payment.new_balance?.toFixed(2) || payment.balance?.toFixed(2)}
                                </td>
                                <td className="px-3 py-2 text-gray-700 text-xs">
                                  {payment.remarks || payment.remark || "—"}
                                </td>
                                {isAdminRole && (
                                  <td className="px-3 py-2">
                                    <RowActionsMenu
                                      isOpen={openPaymentActionId === (payment.id || index)}
                                      onToggle={() =>
                                        setOpenPaymentActionId(
                                          openPaymentActionId === (payment.id || index) ? null : payment.id || index
                                        )
                                      }
                                      onClose={() => setOpenPaymentActionId(null)}
                                      onView={() => navigate(`/payments/view/${payment.id}`)}
                                      onEdit={() => navigate(`/payments/edit/${payment.id}`)}
                                      onDelete={() => handleDeletePayment(payment)}
                                    />
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile cards — same layout as Payment List */}
                      <div className="lg:hidden space-y-3">
                        {paymentHistory.map((payment, index) => (
                          <div key={payment.id || index} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                            <div className="flex justify-between items-start gap-2 mb-3">
                              <div className="min-w-0">
                                <p className="text-xs text-gray-500">Date</p>
                                <p className="font-semibold text-gray-900 text-sm">
                                  {formatIstDateTime(payment.payment_date || payment.created_at)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold uppercase whitespace-nowrap">
                                  {payment.payment_mode?.replace(/_/g, ' ') || payment.type || 'Payment'}
                                </span>
                                {isAdminRole && (
                                  <RowActionsMenu
                                    className="relative menu-container"
                                    isOpen={openPaymentActionId === (payment.id || index)}
                                    onToggle={() =>
                                      setOpenPaymentActionId(
                                        openPaymentActionId === (payment.id || index) ? null : payment.id || index
                                      )
                                    }
                                    onClose={() => setOpenPaymentActionId(null)}
                                    onView={() => navigate(`/payments/view/${payment.id}`)}
                                    onEdit={() => navigate(`/payments/edit/${payment.id}`)}
                                    onDelete={() => handleDeletePayment(payment)}
                                  />
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                              <div>
                                <span className="text-gray-500">Amount:</span>
                                <p className="font-bold text-green-600">
                                  ₹{(payment.payment_amount ?? payment.amount ?? 0).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Prev Balance:</span>
                                <p className="font-semibold text-gray-900">
                                  ₹{(payment.previous_balance ?? 0).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">New Balance:</span>
                                <p className="font-bold text-red-600">
                                  ₹{(payment.new_balance ?? payment.balance ?? 0).toFixed(2)}
                                </p>
                              </div>
                              <div>
                                <span className="text-gray-500">Recorded By:</span>
                                <p className="font-medium text-gray-900 truncate">
                                  {payment.recorded_by_name || 'N/A'}
                                </p>
                              </div>
                              {(payment.remarks || payment.remark) && (
                                <div className="col-span-2">
                                  <span className="text-gray-500">Remarks:</span>
                                  <p className="text-gray-900 text-xs mt-0.5">{payment.remarks || payment.remark}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-5xl mb-4">📭</div>
                      <p className="text-gray-600 font-semibold mb-4">No payment history found</p>
                      {canAddPayment && (
                        <Button variant="primary" onClick={() => navigate(`/payments/add/${shipmentId}`)}>
                          Add First Payment
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Delivery Charge Modal */}
                {showDeliveryChargeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Delivery Charge</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Amount (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={deliveryChargeAmount}
                              onChange={(e) => setDeliveryChargeAmount(e.target.value)}
                              placeholder="Enter amount"
                              className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Payment Mode *</label>
                              <SelectField variant="form"
                              value={deliveryChargePaymentMode}
                              onChange={(e) => setDeliveryChargePaymentMode(e.target.value)}
                              className="px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-500"
                            >
                              {CHARGE_PAYMENT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </SelectField>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <Button variant="secondary" className="flex-1" onClick={() => {
                            setShowDeliveryChargeModal(false)
                            setDeliveryChargeAmount("")
                            setDeliveryChargePaymentMode("cash")
                          }}>
                          Cancel
                        </Button>
                        <Button variant="primary" className="flex-1" onClick={handleAddDeliveryCharge} loading={addingCharge} disabled={addingCharge}>
                          {addingCharge ? "Adding..." : "Add Charge"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Labour Charge Modal */}
                {showLabourChargeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Labour Charge</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Amount (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={labourChargeAmount}
                              onChange={(e) => setLabourChargeAmount(e.target.value)}
                              placeholder="Enter amount"
                              className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Payment Mode *</label>
                              <SelectField variant="form"
                              value={labourChargePaymentMode}
                              onChange={(e) => setLabourChargePaymentMode(e.target.value)}
                              className="px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-purple-500"
                            >
                              {CHARGE_PAYMENT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </SelectField>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <Button variant="secondary" className="flex-1" onClick={() => {
                            setShowLabourChargeModal(false)
                            setLabourChargeAmount("")
                            setLabourChargePaymentMode("cash")
                          }}>
                          Cancel
                        </Button>
                        <Button variant="primary" className="flex-1" onClick={handleAddLabourCharge} loading={addingCharge} disabled={addingCharge}>
                          {addingCharge ? "Adding..." : "Add Charge"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Late Charge Modal */}
                {showLateChargeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Late Charge</h2>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Amount (₹) *</label>
                            <input
                              type="number"
                              step="0.01"
                              value={lateChargeAmount}
                              onChange={(e) => setLateChargeAmount(e.target.value)}
                              placeholder="Enter amount"
                              className="px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="text-sm font-bold text-gray-700 mb-2">Payment Mode *</label>
                              <SelectField variant="form"
                              value={lateChargePaymentMode}
                              onChange={(e) => setLateChargePaymentMode(e.target.value)}
                              className="px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-red-500"
                            >
                              {CHARGE_PAYMENT_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </SelectField>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-6">
                        <Button variant="secondary" className="flex-1" onClick={() => {
                            setShowLateChargeModal(false)
                            setLateChargeAmount("")
                            setLateChargePaymentMode("cash")
                          }}>
                          Cancel
                        </Button>
                        <Button variant="primary" className="flex-1" onClick={handleAddLateCharge} loading={addingLateCharge} disabled={addingLateCharge}>
                          {addingLateCharge ? "Adding..." : "Add Charge"}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {showPaymentModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
                      <h2 className="text-lg font-bold text-gray-800 mb-4">Add Payment</h2>
                      <p className="text-sm text-gray-600 mb-4">
                        Balance Due: <span className="font-bold text-red-600">₹{shipment.balance_amount?.toFixed(2)}</span>
                      </p>
                      <div className="flex gap-3">
                        <Button variant="secondary" className="flex-1" onClick={() => setShowPaymentModal(false)}>
                          Close
                        </Button>
                        <Button variant="success" className="flex-1" onClick={() => navigate(`/payments/add/${shipmentId}`)}>
                          Go to Payment Form
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <ViewPackageDimensionModal
                  isOpen={showViewPackageModal}
                  onClose={() => setShowViewPackageModal(false)}
                  parcels={shipment?.parcels}
                  ratePerKg={shipment?.rate_per_kg}
                />

                <CancelShipmentModal
                  open={cancelModal.open}
                  mode={cancelModal.mode}
                  shipment={shipment}
                  submitting={cancelSubmitting}
                  onClose={() => setCancelModal({ open: false, mode: 'request' })}
                  onSubmit={handleCancelSubmit}
                />

                {showTrackingModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] p-4">
                    <div className="bg-white rounded-xl shadow-lg w-full max-w-xl max-h-[85vh] overflow-hidden flex flex-col">
                      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
                        <h3 className="text-base font-semibold text-gray-800">Shipment Tracking</h3>
                        <Button
                          variant="ghost"
                          onClick={() => setShowTrackingModal(false)}
                          className="!px-2 !py-1"
                        >
                          Close
                        </Button>
                      </div>
                      <div className="overflow-y-auto p-4">
                        {loadingTracking ? (
                          <Loader fullScreen={false} message={t('common.loading')} />
                        ) : trackingError ? (
                          <p className="text-sm text-red-600">{trackingError}</p>
                        ) : (
                          <ShipmentTrackingPanel trackingData={trackingData} timelineOnly />
                        )}
                      </div>
                    </div>
                  </div>
                )}
    </div>
  )
}

export default ViewShipment;